import type ArrayProxy from '@ember/array/proxy';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task, timeout } from 'ember-concurrency';
import type LocationModel from 'garaje/models/location';
import type PluginInstallModel from 'garaje/models/plugin-install';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type CurrentLocationService from 'garaje/services/current-location';
import type DiplomatPluginsService from 'garaje/services/diplomat-plugins';
import type { SupportedPlugin } from 'garaje/services/diplomat-plugins';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type WorkplaceMetricsService from 'garaje/services/workplace-metrics';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import zft from 'garaje/utils/zero-for-tests';

interface DiplomatConfigPairDiplomatClientComponentArgs {
  currentLocation: LocationModel;
  pluginInstalls: ArrayProxy<PluginInstallModel>;
  onClose?: () => unknown;
  onUpdateClient: () => Promise<void>;
}

export default class DiplomatConfigPairDiplomatClientComponent extends Component<DiplomatConfigPairDiplomatClientComponentArgs> {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare currentLocation: CurrentLocationService;
  @service declare diplomatPlugins: DiplomatPluginsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare workplaceMetrics: WorkplaceMetricsService;

  @tracked pairingCode = '';
  @tracked showErrorOnPair = false;
  @tracked selectedIntegration?: SupportedPlugin;
  @tracked internalUrl = '';
  @tracked supportedPlugins: SupportedPlugin[] = [];

  // Pairing code format validation constants
  private readonly PAIRING_CODE_REGEX = /^[A-Z0-9]{8}$/i;
  private readonly EXPECTED_LENGTH = 8;

  constructor(owner: unknown, args: DiplomatConfigPairDiplomatClientComponentArgs) {
    super(owner, args);
    void this.loadSupportedPlugins();
  }

  private async loadSupportedPlugins(): Promise<void> {
    try {
      this.supportedPlugins = await this.diplomatPlugins.supportedPlugins;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load supported plugins');

      this.workplaceMetrics.trackEvent('DIPLOMAT_PLUGINS_LOAD_FAILED', {
        error_message: error instanceof Error ? error.message : String(error),
        component: 'pair-diplomat-client',
        location_id: this.args.currentLocation?.id,
        timestamp: new Date().toISOString(),
      });
      // Fallback handled in service
      this.supportedPlugins = [];
    }
  }

  get diplomatAblePluginInstall(): number | undefined {
    const selectedPluginId = this.selectedIntegration?.id;

    if (!selectedPluginId) {
      return undefined;
    }

    if (!this.args.pluginInstalls || this.args.pluginInstalls.length === 0) {
      return undefined;
    }

    const validPluginInstall = this.args.pluginInstalls.find((pluginInstall: PluginInstallModel) => {
      const isValidStatus = pluginInstall.status === 'active' || pluginInstall.status === 'pending';
      // eslint-disable-next-line ember/use-ember-get-and-set
      const pluginId = pluginInstall.plugin?.get('id');
      const isMatchingPlugin = pluginId === selectedPluginId;

      return isValidStatus && isMatchingPlugin;
    });

    return validPluginInstall ? Number.parseInt(validPluginInstall.id) : undefined;
  }

  get disabled(): boolean {
    const trimmed = this.pairingCode.trim();
    const trimmedUrl = this.internalUrl.trim();
    return (
      !this.selectedIntegration ||
      trimmed.length !== this.EXPECTED_LENGTH ||
      !this.isValidPairingCodeFormat(trimmed) ||
      trimmedUrl.length === 0
    );
  }

  get pairingCodeValidationError(): string | null {
    const trimmed = this.pairingCode.trim();
    if (trimmed.length === 0) return null;

    if (!this.isValidPairingCodeFormat(trimmed)) {
      return this.getPairingCodeValidationMessage(trimmed);
    }

    return null;
  }

  get integrationValidationWarning(): string | null {
    if (!this.selectedIntegration) return null;

    const plugin_install_id = this.diplomatAblePluginInstall;
    if (plugin_install_id === undefined) {
      return `${this.selectedIntegration.name} is not installed at this location yet. You can still pair the client and install the integration later.`;
    }

    return null;
  }

  get internalUrlValidationError(): string | null {
    const trimmed = this.internalUrl.trim();
    if (trimmed.length === 0) {
      return 'Internal URL is required';
    }

    return null;
  }

  /**
   * Validates pairing code format: 8-digit alphanumeric (A-Z, 0-9)
   * Must be exactly 8 characters containing only uppercase letters and numbers
   */
  private isValidPairingCodeFormat(code: string): boolean {
    return this.PAIRING_CODE_REGEX.test(code.toUpperCase());
  }

  /**
   * Provides specific validation error message based on the input
   */
  private getPairingCodeValidationMessage(code: string): string {
    if (code.length !== this.EXPECTED_LENGTH) {
      return `Pairing code must be exactly ${this.EXPECTED_LENGTH} characters (e.g., ABC12345)`;
    }

    // Check for invalid characters
    if (!/^[A-Z0-9]+$/i.test(code)) {
      return 'Pairing code must contain only letters and numbers';
    }

    return 'Invalid pairing code format';
  }

  @action
  onInputCode(event: { target: HTMLInputElement }): void {
    const value = event.target && 'value' in event.target ? event.target['value'] : null;
    if (!value?.trim()) {
      this.pairingCode = '';
      this.showErrorOnPair = false;
      return;
    }

    // Format input: uppercase, trim whitespace, and limit to 8 characters
    let formattedValue = value.trim().toUpperCase();

    // Remove any non-alphanumeric characters
    formattedValue = formattedValue.replace(/[^A-Z0-9]/g, '');

    // Limit to 8 characters
    if (formattedValue.length > this.EXPECTED_LENGTH) {
      formattedValue = formattedValue.substring(0, this.EXPECTED_LENGTH);
    }

    this.pairingCode = formattedValue;
    this.showErrorOnPair = false;

    // Update the input field to reflect formatting
    if (event.target && formattedValue !== value) {
      event.target.value = formattedValue;
    }
  }

  @action
  setSelectedIntegration(option: SupportedPlugin): void {
    this.selectedIntegration = option;
  }

  @action
  onInputInternalUrl(event: { target: HTMLInputElement }): void {
    const value = event.target && 'value' in event.target ? event.target['value'] : '';
    this.internalUrl = value || '';
  }

  @action
  async submitPairingCode(): Promise<void> {
    if (!this.disabled) {
      // Pass the current pairingCode - it will be normalized to uppercase before sending
      await this.pairDiplomatClientTask.perform(this.pairingCode);
    }
  }

  pairDiplomatClientTask = task(async (code: string) => {
    if (this.disabled) return;

    this.showErrorOnPair = false;
    await timeout(zft(500));

    try {
      // Check if current location is available
      if (!this.currentLocation.location) {
        throw new Error('Current location is not available');
      }

      // Get plugin install ID (may be undefined if integration not installed yet)
      const plugin_install_id = this.diplomatAblePluginInstall;

      if (!this.selectedIntegration) {
        throw new Error('Please select an integration to pair with');
      }

      // Ensure pairing code is uppercase before sending to server
      const normalizedCode = code.trim().toUpperCase();

      // Ensure locationId is sent as a number, not a string
      const locationId = Number(this.currentLocation.location.id);
      if (isNaN(locationId)) {
        throw new Error(`Invalid location ID: ${this.currentLocation.location.id}`);
      }

      // Build request payload and ensure locationId is a number
      const requestPayload = {
        pairingCode: normalizedCode,
        locationId: locationId + 0, // Force to number with arithmetic operation
        pluginInstallId: plugin_install_id || null, // null if integration not installed yet
        pluginId: this.selectedIntegration.id,
        internalUrl: this.internalUrl.trim(), // Internal URL is required
      };

      const host = this.diplomatPlugins.getDiplomatServerHost();
      const url = urlBuilder.diplomatServer.pairDiplomatClientUrl(host);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        // Handle specific error response from diplomat-server
        let errorMessage = `Failed to pair client`;
        try {
          const errorData = (await response.json()) as { error?: string; message?: string };
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // If JSON parsing fails, use default error message
        }
        throw new Error(errorMessage);
      }

      this.workplaceMetrics.trackEvent('LOCATION_SETTINGS_BINARY_DOWNLOAD_DIPLOMAT_CLIENT_PAIRED', {
        pairing_code: normalizedCode,
        location_id: parseInt(this.currentLocation.location.id, 10),
        plugin_id: this.selectedIntegration.id,
        plugin_install_id: plugin_install_id || null,
      });

      await this.args.onUpdateClient();
      this.onClose();
      this.flashMessages.showAndHideFlash('success', 'Client successfully paired.');
    } catch (e) {
      this.showErrorOnPair = true;
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  @action
  onClose(): void {
    this.args.onClose?.();
  }
}
